import { useEffect, useRef } from 'react';

const FOCUSABLE = 'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled)';

/**
 * Keyboard behaviour for a modal dialog: Escape calls onEscape, and Tab stays
 * inside the dialog. Focus returns to whatever had it when the dialog closes.
 *
 * @param {Object} dialogRef - Ref to the dialog element
 * @param {Function} onEscape - Called when Escape is pressed
 * @param {boolean} active - False while another dialog is open on top of this one
 */
export default function useDialogKeys(dialogRef, onEscape, active = true) {
  const latest = useRef(onEscape);
  latest.current = onEscape;
  const isActive = useRef(active);
  isActive.current = active;

  useEffect(() => {
    const opener = document.activeElement;
    const onKey = (e) => {
      if (!isActive.current) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        latest.current?.();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll(FOCUSABLE)];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
    };
  }, [dialogRef]);
}
